<template>
  <div
    id="accountInfo"
    ref="accountInfoSection"
  >
    <div style="display: flex; align-items: center">
      <h2 class="account-card-title">Account Info</h2>
      <div style="width: 25px; height: 18px; padding-left: 8px">
        <Pencil
          :fill="mainColor"
          class="cursor-pointer"
          @click="editAccountInfo"
          v-if="!edit.accountInfo"
          data-test="edit-button"
        />
      </div>
    </div>

    <Card
      class="account-card-info card-style"
      v-if="!edit.accountInfo"
    >
      <template #content>
        <div
          v-if="
            donorStore.donorInfo.prefix ||
            donorStore.donorInfo.firstName ||
            donorStore.donorInfo.lastName ||
            donorStore.donorInfo.suffix
          "
        >
          <div class="field-title">Name</div>
          <div
            class="field"
            data-test="name-field"
          >
            {{ donorStore.donorInfo.prefix }} {{ donorStore.donorInfo.firstName }} {{ donorStore.donorInfo.lastName }}
            {{ donorStore.donorInfo.suffix }}
          </div>
        </div>
        <div v-if="donorStore.donorInfo.pronouns">
          <div class="field-title">Pronouns</div>
          <div class="field">{{ donorStore.donorInfo.pronouns }}</div>
        </div>
        <div>
          <div class="field-title">Email</div>
          <div
            class="field"
            data-test="email-field"
          >
            {{ donorStore.donorInfo.email }}
          </div>
        </div>
        <div v-if="donorStore.donorInfo.homePhoneNumber">
          <div class="field-title">Phone</div>
          <div
            class="field"
            data-test="phone-field"
          >
            {{ donorStore.donorInfo.homePhoneNumber }}
          </div>
        </div>
        <div v-if="donorStore.donorInfo.addressLine1 || donorStore.donorInfo.city || donorStore.donorInfo.state || donorStore.donorInfo.stateOther || donorStore.donorInfo.zip">
          <div class="field-title">Address</div>
          <div
            class="field"
            style="text-align: left"
            v-html="formattedAddress"
            data-test="address-field"
          ></div>
        </div>
      </template>
    </Card>

    <Card
      class="account-card-edit-info-grid card-style"
      v-if="edit.accountInfo"
    >
      <template #content>
        <AccountInfoForm width="400px"></AccountInfoForm>
        <div style="display: flex; justify-content: space-between; grid-column: span 2; height: 50px">
          <Button
            class="p-button-text"
            :style="{ color: donorStore.mainColor }"
            @click="cancelAccountInfo"
            data-test="cancel-button"
          >
            Cancel
          </Button>
          <Button
            class="p-button"
            :style="{ width: '168px', color: getContrastYIQ(donorStore.mainColor) }"
            @click="updateAccountInfo"
            data-test="save-button"
          >
            Save Changes
          </Button>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import Pencil from '@/assets/Pencil.vue';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import AccountInfoForm from '@/components/settings/AccountInfoForm.vue';
import isMobile from '@/composables/useIsMobile.js';

const donorStore = useDonorStore();
const mainColor = donorStore.mainColor;
donorStore.fetchStateCountry();

const donorId = donorStore.donorInfo.donorAccounts_id;

const edit = ref({
  accountInfo: false,
});

let isUpdatingState = false;
let isUpdatingStateOther = false;

watch(
  () => donorStore.donorInfo.state,
  (newState, oldState) => {
    if (newState !== oldState) {
      if (isUpdatingState) {
        isUpdatingState = false;
      } else {
        isUpdatingStateOther = true;
        donorStore.donorInfo.stateOther = null;
        nextTick(() => {
          isUpdatingState = false;
          isUpdatingStateOther = false;
        });
      }
    }
  },
);

watch(
  () => donorStore.donorInfo.stateOther,
  (newStateOther, oldStateOther) => {
    if (newStateOther !== oldStateOther) {
      if (isUpdatingStateOther) {
        isUpdatingStateOther = false;
      } else {
        isUpdatingState = true;
        donorStore.donorInfo.state = null;
        nextTick(() => {
          isUpdatingState = false;
          isUpdatingStateOther = false;
        });
      }
    }
  },
);

const formattedAddress = computed(() => {
  donorStore.updateStateCode();

  let formattedAddress = donorStore.donorInfo.addressLine1 ? `${donorStore.donorInfo.addressLine1}, ` : '';

  if (donorStore.donorInfo.addressLine2) {
    formattedAddress += donorStore.donorInfo.addressLine2 ? `${donorStore.donorInfo.addressLine2} <br>` : '';
  }

  let city = donorStore.donorInfo.city ? `${donorStore.donorInfo.city}, ` : '';
  let stateCode = donorStore.stateCode ? `${donorStore.stateCode} ` : '';
  if (!stateCode) {
    stateCode = donorStore.donorInfo.stateOther ? `${donorStore.donorInfo.stateOther} ` : '';
  }
  let zip = donorStore.donorInfo.zip ? donorStore.donorInfo.zip : '';
  formattedAddress += `${city}${stateCode}${zip}`;

  return formattedAddress;
});

const editAccountInfo = () => {
  edit.value.accountInfo = true;
};

const cancelAccountInfo = () => {
  edit.value.accountInfo = false;
};

const updateAccountInfo = () => {
  donorStore.updateDonorAccount(donorId, {
    prefix: donorStore.donorInfo.prefix,
    firstName: donorStore.donorInfo.firstName,
    middleName: donorStore.donorInfo.middleName,
    lastName: donorStore.donorInfo.lastName,
    suffix: donorStore.donorInfo.suffix,
    pronouns: donorStore.donorInfo.pronouns,
    email: donorStore.donorInfo.email,
    homePhoneNumber: donorStore.donorInfo.homePhoneNumber,
    addressLine1: donorStore.donorInfo.addressLine1,
    addressLine2: donorStore.donorInfo.addressLine2,
    city: donorStore.donorInfo.city,
    state: donorStore.donorInfo.state,
    stateOther: donorStore.donorInfo.stateOther,
    zip: donorStore.donorInfo.zip,
    country: donorStore.donorInfo.country,
  });
  edit.value.accountInfo = false;
};
</script>
<style lang="scss">
.field-title {
  color: #000;
  text-align: center;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: left;
}
.field {
  color: #000;
  text-align: left;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
.card-info {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

// .input {
//   padding: 0 4px 24px 4px;
//   width: 400px;
// }
// @media screen and (max-width: 1440px) {
//   .input {
//     width: 560px;
//   }
// }
</style>
